import { Injectable } from '@angular/core';
import { Logger } from '@mode/shared/contract-common';
import { generateUUID, StringHelpers } from '@mode/shared/util-js';
import * as angular from 'angular';
import { merge, some } from 'lodash';
import { Resource } from '@mode/shared/util-halogen';
import { QAdapterService } from '../migration/q-adapter.service';
import { extend } from './extend';
import { ModeCollectionFactoryService } from './model-collection.factory';
import { ModelNameService } from './model-name.service';
import { ModelAccessorBase } from './model_accessor';

interface AttributeProperties {
  name: string;
  default?: any;
  abstract?: boolean;
  type?: string;
  preserveCase?: boolean;
}

interface HasOneProperties {
  name: string;
  idAttribute?: string;
  as?: string;
}

interface HasManyProperties {
  name: string;
  idAttribute?: string;
  as?: string;
}

interface HasPolyProperties {
  name: string;
  id: string;
  type: string;
}

export class ModeModelBase {
  _name = 'model';
  idAttribute = 'id';
  parentModel: string = null;
  parentIdAttribute = 'id';

  _resource: any;
  mapping_id: any;
  attributes?: AttributeProperties[];
  _collections: any[];
  hasMany: HasManyProperties[];
  hasOne: HasOneProperties[];
  hasPoly: HasPolyProperties[];
  belongsTo: string;
  belongsToAccessor: ModelAccessorBase<any>;

  parent: string;
  _formElements: any;

  constructor(protected injectables: ModeModelInjectables) {}

  $initialize(resource, opts?) {
    this._resource = resource;

    if (angular.isDefined(resource)) {
      this[this.idAttribute] = resource[this.idAttribute];
      this.mapping_id = resource.mapping_id;
    }

    this._initHasManyRelationships(opts && opts.ignoreEmbedded);

    this._initHasOneRelationships(opts && opts.ignoreEmbedded);

    this._initHasPolyRelationships();

    this._initBelongsToRelationship(opts && opts.ignoreEmbedded);

    this._initAttributes(resource);

    if (
      (angular.isUndefined(this[this.idAttribute]) || this[this.idAttribute] === null) &&
      this._resource &&
      angular.isFunction(this._resource.hasLink) &&
      this._resource.hasLink('self')
    ) {
      this[this.idAttribute] = this._resource.links('self').href;
    } else if (angular.isUndefined(this[this.idAttribute]) || this[this.idAttribute] === null) {
      this.injectables.logger.debug('model has no id, generating uuid for', this);
      this[this.idAttribute] = generateUUID();
    }

    this.initialize(resource);

    if (this._resource) {
      this.injectables.logger.debug('created model', this);
    }
  }

  initialize(resource) {
    // Define for overriding
  }

  toParams() {
    const params = {};
    let attributePathName;

    const sanitize = (object, path, preserveCase) => {
      if (angular.isObject(object) && !angular.isArray(object)) {
        angular.forEach(object, (value, key) => {
          if (path) {
            sanitize(value, path + '.' + (preserveCase ? key : StringHelpers.underscore(key)), false);
          } else {
            sanitize(value, key, preserveCase);
          }
        });
      } else {
        params[path] = object;
      }
    };

    angular.forEach(
      this.attributes,
      (attribute) => {
        attributePathName = StringHelpers.underscore(this._name) + '.' + StringHelpers.underscore(attribute.name);

        if (!attribute.abstract) {
          if (
            angular.isObject(this[attribute.name]) &&
            !angular.isArray(this[attribute.name]) &&
            attribute.type !== 'text'
          ) {
            sanitize(this[attribute.name], attributePathName, attribute.preserveCase);
          } else if (
            angular.isObject(this[attribute.name]) &&
            !angular.isArray(this[attribute.name]) &&
            attribute.type === 'text'
          ) {
            params[attributePathName] = JSON.stringify(this[attribute.name]);
          } else {
            params[attributePathName] = this[attribute.name];
          }
        }
      },
      this
    );
    return params;
  }

  extendAttributes(model) {
    model._resource._links && angular.extend(this._resource._links, model._resource._links);
    model._resource._embedded && angular.extend(this._resource._embedded, model._resource._embedded);
    model._resource._forms && angular.extend(this._resource._forms, model._resource._forms);

    this._collections = this.injectables.modeCollectionFactoryService.extendModelCollections(
      this._collections,
      model._collections
    );

    angular.forEach(
      this.attributes,
      (attribute) => {
        const resourceName = StringHelpers.underscore(attribute.name);
        if (model._resource && angular.isDefined(model._resource[resourceName]) && !attribute.abstract) {
          this._resource[resourceName] = model._resource[resourceName];
        }
      },
      this
    );

    this._initAttributes(this._resource);

    if (angular.isFunction(this.initialize)) {
      this.initialize.call(this, this._resource);
    }
  }

  _initAttributes(resource) {
    angular.forEach(
      this.attributes,
      (attribute) => {
        const resourceName = StringHelpers.underscore(attribute.name);

        if (
          resource &&
          angular.isDefined(resource[resourceName]) &&
          angular.isObject(resource[resourceName]) &&
          !angular.isArray(resource[resourceName])
        ) {
          this[attribute.name] = angular.copy(resource[resourceName]);
        } else if (resource && angular.isDefined(resource[resourceName])) {
          this[attribute.name] = resource[resourceName];
        } else if (resource && angular.isFunction(resource.hasLink) && resource.hasLink(attribute.name)) {
          this[attribute.name] = resource.links(attribute.name).href;
        } else if (angular.isObject(attribute['default']) && !angular.isArray(attribute['default'])) {
          this[attribute.name] = angular.copy(attribute['default']);
        } else if (!attribute.abstract) {
          this[attribute.name] = attribute['default'];
        }
      },
      this
    );
  }

  _initHasManyRelationships(ignoreEmbedded) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    angular.forEach(
      this.hasMany,
      (relationship) => {
        if (angular.isString(relationship.name)) {
          const name = relationship.as ? relationship.as : relationship.name;

          const singularName = StringHelpers.singularize(name);
          const singularRelationshipName = StringHelpers.singularize(relationship.name);
          this[name] = (id, params) => {
            if (angular.isString(id) || angular.isNumber(id)) {
              return self.$get(id, StringHelpers.underscore(singularName), relationship.idAttribute, params);
            } else if (self._resource?.hasLink && self._resource.hasLink(StringHelpers.underscore(name))) {
              const params = angular.isObject(id) ? id : undefined;
              return this.injectables.modeCollectionFactoryService.find(
                self._resource.links(StringHelpers.underscore(name)).href,
                singularRelationshipName,
                params
              );
            } else {
              const err: any = new Error('no link found for collection ' + name);
              err.silent = true;
              return this.injectables.$q.reject(err);
            }
          };

          this['new_' + name] = () => self.findEmbedded(null, 'new_' + StringHelpers.underscore(singularName));

          this['modelFromForm_' + name] = () => {
            const embedded = self.findEmbedded(null, 'new_' + StringHelpers.underscore(singularName));

            if (!embedded || !embedded.hasForm('create')) {
              this.injectables.logger.warn('no embedded create form for ' + name);
              return;
            }
            const model = this.injectables.modelNameService.get(singularRelationshipName);
            const newModel = model.createModel(undefined);
            newModel['_new_' + singularRelationshipName] = true;
            newModel._formElements =
              embedded.forms('create')._inputs[StringHelpers.underscore(singularRelationshipName)];
            merge(newModel, embedded.forms('create').$serialize()[StringHelpers.underscore(singularRelationshipName)]);
            return newModel;
          };

          this['canCreateFromForm_' + name] = () => {
            const embedded = self.findEmbedded(null, 'new_' + StringHelpers.underscore(singularName));
            return embedded && embedded.hasForm('create');
          };

          this['validate_' + name] = () => {
            return self.findEmbedded(null, 'validate_' + StringHelpers.underscore(singularName));
          };

          if (!ignoreEmbedded) {
            this.initEmbeddedCollection.call(
              self,
              StringHelpers.underscore(name),
              relationship.name,
              StringHelpers.camalize(singularRelationshipName),
              this.injectables.modelNameService.get(singularRelationshipName) &&
                this.injectables.modelNameService.get(singularRelationshipName).parentModel === this._name
            );
          }
        }
      },
      this
    );
  }

  _initHasOneRelationships(ignoreEmbedded) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    angular.forEach(
      this.hasOne,
      (relationship) => {
        if (angular.isString(relationship.name)) {
          const name = relationship.as ? relationship.as : relationship.name;
          this[name] = (options) => {
            const deferred = this.injectables.$q.defer();
            if (
              self._resource &&
              angular.isFunction(self._resource.hasLink) &&
              self._resource.hasLink(StringHelpers.underscore(name))
            ) {
              const model = this.injectables.modelNameService.get(StringHelpers.singularize(relationship.name));
              const params = options || {};
              const tempLink = self._resource.links(StringHelpers.underscore(name)).href;
              params[model.idAttribute] = model.getIdFromLink ? model.getIdFromLink(tempLink) : tempLink;
              if (!model.getIdFromLink) {
                params.permalink = tempLink;
              }

              model
                .find(params)
                .then(deferred.resolve)
                .catch(() => {
                  self
                    .findLinked(null, StringHelpers.underscore(relationship.name), options)
                    .then((resource: Resource) => {
                      const newModel = model.createModel(resource);
                      const link = self._resource.links(StringHelpers.underscore(relationship.name)).href;
                      newModel.parent = self[self.idAttribute];
                      newModel[model.idAttribute] = model.getIdFromLink ? model.getIdFromLink(link) : link;
                      model.putLocal(newModel);
                      deferred.resolve(newModel);
                    })
                    .catch(deferred.reject);
                });
            } else {
              const err: any = new Error('Model ' + self._name + ' has no ' + relationship.name + ' as ' + name);
              err.type = 'no-resource';
              err.silent = true;
              deferred.reject(err);
            }

            return deferred.promise;
          };

          const pluralName = StringHelpers.pluralize(name);
          this['new_' + pluralName] = () => {
            return self.findEmbedded(null, 'new_' + StringHelpers.underscore(name));
          };

          this['canCreateFromForm_' + pluralName] = () => {
            const embedded = self.findEmbedded(null, 'new_' + StringHelpers.underscore(name));
            return embedded && embedded.hasForm('create');
          };

          if (!ignoreEmbedded) {
            this.initEmbedded.call(self, StringHelpers.underscore(name), StringHelpers.camalize(relationship.name));
          }
        }
      },
      this
    );
  }

  _initHasPolyRelationships() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    angular.forEach(
      this.hasPoly,
      (relationship) => {
        if (
          angular.isDefined(relationship.id) &&
          angular.isString(relationship.type) &&
          angular.isString(relationship.name)
        ) {
          this[relationship.name] = () => {
            const model = this.injectables.modelNameService.get(StringHelpers.camalize(self[relationship.type]));
            if (!model) {
              const err: any = new Error(
                'Model ' +
                  self._name +
                  ' has no ' +
                  relationship.name +
                  ' (' +
                  relationship.type +
                  ' is ' +
                  self[relationship.type] +
                  ')'
              );
              err.silent = true;
              return this.injectables.$q.reject(err);
            }
            const deferred = this.injectables.$q.defer();
            const params = {};
            params[model.idAttribute] = self[relationship.id];

            model
              .find(params)
              .then(deferred.resolve)
              .catch(() => {
                if (
                  self._resource &&
                  angular.isFunction(self._resource.hasLink) &&
                  self._resource.hasLink(StringHelpers.underscore(relationship.name)) &&
                  model
                ) {
                  self
                    .findLinked(null, StringHelpers.underscore(relationship.name))
                    .then((resource: Resource) => {
                      const newModel = model.createModel(resource);
                      model.putLocal(newModel);
                      deferred.resolve(newModel);
                    })
                    .catch(deferred.reject);
                } else {
                  const err: any = new Error(
                    'Model ' + self._name + ' has no ' + relationship.name + ' of type ' + self[relationship.type]
                  );
                  err.silent = true;
                  deferred.reject(err);
                }
              });

            return deferred.promise;
          };
        }
      },
      this
    );
  }

  _initBelongsToRelationship(ignoreEmbedded) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    if (angular.isString(this.belongsTo)) {
      this.belongsToAccessor = this.injectables.modelNameService.get(this.belongsTo);
    }

    if (angular.isDefined(this.belongsToAccessor)) {
      this.parentIdAttribute = this.belongsToAccessor.idAttribute;
      this[this.belongsToAccessor.name] = () => {
        const opts = {};
        let deferred;
        if (self.parent) {
          opts[self.parentIdAttribute] = this.parent;
          deferred = this.injectables.$q.defer();
          self.belongsToAccessor
            .find(opts)
            .then((model) => {
              deferred.resolve(model);
            })
            .catch(() => {
              self
                .findLinked(null, StringHelpers.underscore(self.belongsToAccessor.name))
                .then((resource: Resource) => {
                  const newModel = self.belongsToAccessor.createModel(resource);
                  self.parent = newModel[newModel.idAttribute];
                  self.belongsToAccessor.putLocal(newModel);
                  deferred.resolve(newModel);
                })
                .catch(deferred.reject);
            });

          return deferred.promise;
        } else {
          deferred = this.injectables.$q.defer();
          if (self.belongsToAccessor.getIdFromLink) {
            const params = {};
            const tempLink = self._resource.links(StringHelpers.underscore(self.belongsToAccessor.name)).href;
            params[self.belongsToAccessor.idAttribute] = self.belongsToAccessor.getIdFromLink(tempLink);

            self.belongsToAccessor
              .find(params)
              .then(deferred.resolve)
              .catch(() => {
                self
                  .findLinked(null, StringHelpers.underscore(self.belongsToAccessor.name))
                  .then((resource: Resource) => {
                    const newModel = self.belongsToAccessor.createModel(resource);
                    self.parent = newModel[newModel.idAttribute];
                    self.belongsToAccessor.putLocal(newModel);
                    deferred.resolve(newModel);
                  })
                  .catch(deferred.reject);
              });
          } else {
            self
              .findLinked(null, StringHelpers.underscore(self.belongsToAccessor.name))
              .then((resource: Resource) => {
                const newModel = self.belongsToAccessor.createModel(resource);
                self.parent = newModel[newModel.idAttribute];
                self.belongsToAccessor.putLocal(newModel);
                deferred.resolve(newModel);
              })
              .catch(deferred.reject);
          }
          return deferred.promise;
        }
      };

      if (!ignoreEmbedded) {
        this.initEmbedded.call(
          self,
          StringHelpers.underscore(this.belongsToAccessor.name),
          StringHelpers.camalize(this.belongsToAccessor.name),
          true
        );
      }
    }
  }

  initEmbedded(relation, modelName, isParent) {
    if (this._resource && angular.isFunction(this._resource.hasEmbedded) && this._resource.hasEmbedded(relation)) {
      const embedded = this._resource.embedded(relation);
      const modelClassName = modelName || StringHelpers.camalize(StringHelpers.singularize(relation));
      const model = this.injectables.modelNameService.get(modelClassName);

      if (angular.isUndefined(model)) {
        this.injectables.logger.warn('Cannot intialize unknown embedded model ' + modelClassName + ' in ' + this._name);
        return;
      }

      if (angular.isArray(embedded)) {
        angular.forEach(
          embedded,
          (resource: Resource) => {
            const newModel = model.createModel(resource);
            if (isParent) {
              this.parent = newModel[newModel.idAttribute];
            } else {
              newModel.parent = this[this.idAttribute];
            }
            model.putLocal(newModel);
          },
          this
        );
      } else {
        const newModel = model.createModel(embedded);
        if (isParent) {
          this.parent = newModel[newModel.idAttribute];
        } else if (newModel.belongsTo && newModel.belongsToAccessor.name === this._name) {
          newModel.parent = this[this.idAttribute];
        }
        model.putLocal(newModel);
      }
    }
  }

  initEmbeddedCollection(relationAs, relation, modelName, isParent) {
    if (this._resource && angular.isFunction(this._resource.hasEmbedded) && this._resource.hasEmbedded(relationAs)) {
      const collection = this.injectables.modeCollectionFactoryService.create(
        this._resource.embedded(relationAs),
        modelName || StringHelpers.camalize(StringHelpers.singularize(relation)),
        isParent ? this : undefined,
        relation,
        relationAs
      );
      this.injectables.modeCollectionFactoryService.put(collection);
    }
  }

  findEmbedded(id, relation, idAttribute?) {
    let found;
    const relationName = relation || this._name;

    if (angular.isFunction(this._resource.hasEmbedded) && this._resource.hasEmbedded(relationName)) {
      const embedded = this._resource.embedded(relationName);

      if (angular.isArray(embedded)) {
        angular.forEach(embedded, (embedded) => {
          if (embedded[idAttribute || 'id'] == id) {
            found = embedded;
          }
        });
      } else if (embedded[idAttribute || 'id'] == id) {
        found = embedded;
      }
    }

    return found;
  }

  findLinked(id, relation, params?) {
    const deferred = this.injectables.$q.defer();
    const relationName = relation || this._name;

    if (angular.isFunction(this._resource.hasLink) && this._resource.hasLink(relationName)) {
      const link = this._resource.links(relationName);
      const opts = params && params.includes ? angular.extend({ id: id }, { includes: params.includes }) : { id: id };
      link.$follow(opts).then(deferred.resolve).catch(deferred.reject);
    } else {
      const err: any = new Error('Model ' + this._name + ' has no ' + relationName);
      err.silent = true;
      deferred.reject(err);
    }

    return deferred.promise;
  }

  $get(id, relation, idAttribute, params) {
    const deferred = this.injectables.$q.defer();

    if (angular.isDefined(this._resource)) {
      const found = params && params.includes ? false : this.findEmbedded(id, relation, idAttribute);
      if (found) {
        deferred.resolve(found);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        this.findLinked(id, StringHelpers.singularize(relation), params)
          .then((found) => {
            deferred.resolve(found);
          })
          .catch(deferred.reject);
      }
    } else {
      deferred.reject(new Error('Not Found'));
    }

    return deferred.promise;
  }

  reload(params) {
    if (this._resource && angular.isFunction(this._resource.hasLink) && this._resource.hasLink('self')) {
      const link = this._resource.links('self');
      return link.$follow(params);
    } else {
      return this.injectables.$q.reject(new Error('No link to model found'));
    }
  }

  destroy(options) {
    const deferred = this.injectables.$q.defer();

    if (
      !this['_new_' + this._name] &&
      angular.isDefined(this._resource) &&
      this._resource.hasForm('destroy') &&
      (!options || !options.silent)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      this._resource
        .forms('destroy')
        .$submit()
        .then(() => {
          const modelAccessor = this.injectables.modelNameService.get(self._name);
          modelAccessor.destroy(self);
          deferred.resolve();
        })
        .catch(deferred.reject);
    } else {
      const modelAccessor = this.injectables.modelNameService.get(this._name);
      modelAccessor.destroy(this);
      deferred.resolve();
    }

    return deferred.promise;
  }

  addToCollection(collectionId) {
    if (some(this._collections, { id: collectionId })) {
      return; // no need to add it already has this collection id
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.injectables.modeCollectionFactoryService
      .find(collectionId, this._name)
      .then((collection) => {
        collection.insert(self);
      })
      .catch((e) => {
        this.injectables.logger.log('Unable to find collection ' + collectionId + ' for model ' + self._name);
      });
  }
}

// We could have passed these in the constructor, but there's a lot of these to migrate,
// and only injecting one object will create less mistakes.
@Injectable({ providedIn: 'root' })
export class ModeModelInjectables {
  constructor(
    public logger: Logger,
    public modeCollectionFactoryService: ModeCollectionFactoryService,
    public modelNameService: ModelNameService,
    public $q: QAdapterService
  ) {}
}

/**
 *
 * @deprecated Extend ModeModel class directly instead
 */
export function ModeModelAngularJsMigrationAdapter(modeModelInjectables: ModeModelInjectables) {
  const ModeModelExtender: any = function (resource) {
    // eslint-disable-next-line prefer-spread, prefer-rest-params
    this.$initialize.apply(this, arguments);
  };
  ModeModelExtender.prototype = new ModeModelBase(modeModelInjectables);
  ModeModelExtender.extend = extend;
  return ModeModelExtender;
}
ModeModelAngularJsMigrationAdapter.$inject = ['ModeModelInjectables'];
